import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n<ul class=\"cNA--flyout cNA--flyout--v2 qa--listenersFlyout {{if @showFlyout 'is--open'}}\">\n  {{#if this.loadData.isRunning}}\n    <div class=\"cNA--list__listenersList cCH--loading\"></div>\n    <div class=\"cNA--list__listenersList cCH--loading\"></div>\n    <div class=\"cNA--list__listenersList cCH--loading\"></div>\n  {{else}}\n    {{! Social Listening }}\n    <li class=\"cNA--flyout__item\">\n      <ul class=\"cNA--flyout__ul\" {{on \"click\" @toggleFlyout}}>\n        {{#each this.socialListeningLinks as |navItem|}}\n          <li class={{if navItem.sectionLabel \"cNA--flyout__main-link\"}}>\n            {{#if (and navItem.sectionLabel)}}\n              <div class=\"cNA--flyout__main-link-title\">\n                {{navItem.sectionLabel}}\n              </div>\n            {{/if}}\n            {{#each navItem.links as |link|}}\n              {{#if link.isVisible}}\n                <Nav::Subnav::Listening::Link @link={{link}} class=\"cNA--flyout__link {{link.qaClass}}\" />\n              {{/if}}\n            {{/each}}\n          </li>\n        {{/each}}\n      </ul>\n    </li>\n  {{/if}}\n</ul>", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n<ul class=\"cNA--flyout cNA--flyout--v2 qa--listenersFlyout {{if @showFlyout 'is--open'}}\">\n  {{#if this.loadData.isRunning}}\n    <div class=\"cNA--list__listenersList cCH--loading\"></div>\n    <div class=\"cNA--list__listenersList cCH--loading\"></div>\n    <div class=\"cNA--list__listenersList cCH--loading\"></div>\n  {{else}}\n    {{! Social Listening }}\n    <li class=\"cNA--flyout__item\">\n      <ul class=\"cNA--flyout__ul\" {{on \"click\" @toggleFlyout}}>\n        {{#each this.socialListeningLinks as |navItem|}}\n          <li class={{if navItem.sectionLabel \"cNA--flyout__main-link\"}}>\n            {{#if (and navItem.sectionLabel)}}\n              <div class=\"cNA--flyout__main-link-title\">\n                {{navItem.sectionLabel}}\n              </div>\n            {{/if}}\n            {{#each navItem.links as |link|}}\n              {{#if link.isVisible}}\n                <Nav::Subnav::Listening::Link @link={{link}} class=\"cNA--flyout__link {{link.qaClass}}\" />\n              {{/if}}\n            {{/each}}\n          </li>\n        {{/each}}\n      </ul>\n    </li>\n  {{/if}}\n</ul>","moduleName":"later/components/nav/subnav/listening.hbs","parseOptions":{"srcName":"later/components/nav/subnav/listening.hbs"}});
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';

import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type BrandProfileModel from 'later/models/brand-profile';
import type GroupModel from 'later/models/group';
import type AppHeaderService from 'later/services/app-header';
import type AuthService from 'later/services/auth';
import type { Maybe } from 'shared/types';

interface ListeningComponentArgs {
  showFlyout: boolean;
  toggleFlyout: () => void;
}

interface SocialListeningLinkMenuItem {
  label: string;
  route: string;
  icon: string;
  isVisible: boolean;
  isNew: boolean;
  dataTestId: string;
  qaClass: string;
  query?: Record<string, string>;
}

interface SocialListeningSubNavLinks {
  sectionLabel: string | null;
  links: SocialListeningLinkMenuItem[];
}

interface LinksArgs {
  isSocialListeningVisible: boolean;
  isFutureInsightsVisible: boolean;
  isMentionsVisible: boolean;
  isSentimentVisible: boolean;
  isBrandDnaVisible: boolean;
  isCompetitiveBenchmarkingVisible: boolean;
}

export const socialListeningLinks = (
  intl: IntlService,
  {
    isSocialListeningVisible,
    isFutureInsightsVisible,
    isMentionsVisible,
    isSentimentVisible,
    isBrandDnaVisible,
    isCompetitiveBenchmarkingVisible
  }: LinksArgs,
  overrideBenchmarkingLabel?: string
): SocialListeningSubNavLinks[] => {
  const navLinks = [
    {
      sectionLabel: null,
      links: [
        {
          label: intl.t('header.navigation.social_listening.topics'),
          route: 'cluster.social-listening.topics',
          icon: 'light-bulb',
          isVisible: isSocialListeningVisible,
          isNew: false,
          dataTestId: 'qa--topics_nav_link',
          qaClass: 'qa--nav__link--topics'
        }
      ]
    },
    {
      sectionLabel: null,
      links: [
        {
          label: intl.t('header.navigation.social_listening.future_insights'),
          route: 'cluster.social-listening.future-insights',
          icon: 'trending-up',
          isVisible: isSocialListeningVisible || isFutureInsightsVisible,
          isNew: false,
          dataTestId: 'qa--future_insights_nav_link',
          qaClass: 'qa--nav__link--future-insights '
        }
      ]
    },
    {
      sectionLabel: intl.t('header.navigation.social_listening.my_brand'),
      links: [
        {
          label: intl.t('header.navigation.social_listening.sentiment'),
          route: 'cluster.social-listening.brands',
          query: { tab: 'health' },
          icon: 'sentiment',
          isVisible: isSocialListeningVisible || isSentimentVisible,
          isNew: false,
          dataTestId: 'qa--sentiment_nav_link',
          qaClass: 'qa--nav__link--sentiment'
        },
        {
          label: intl.t('header.navigation.social_listening.mentions'),
          route: 'cluster.social-listening.brands',
          query: { tab: 'mentions' },
          icon: 'mention',
          isVisible: isMentionsVisible,
          isNew: false,
          dataTestId: 'qa--mentions_nav_link',
          qaClass: 'qa--nav__link--mentions'
        }
      ]
    },
    {
      sectionLabel: intl.t('header.navigation.social_listening.my_competitors'),
      links: [
        {
          label: intl.t('header.navigation.social_listening.brand_dna'),
          route: 'cluster.social-listening.brand-dna',
          icon: 'audience-insights',
          isVisible: isSocialListeningVisible && isBrandDnaVisible,
          isNew: true,
          dataTestId: 'qa--brand-dna_nav_link',
          qaClass: 'qa--nav__link--brand-dna'
        },
        {
          label: intl.t(overrideBenchmarkingLabel ?? 'header.navigation.social_listening.benchmarking'),
          route: 'cluster.social-listening.competitors',
          icon: 'audience',
          isVisible: isSocialListeningVisible || isCompetitiveBenchmarkingVisible,
          isNew: false,
          dataTestId: 'qa--competitors_nav_link',
          qaClass: 'qa--nav__link--competitors'
        }
      ]
    }
  ];

  return navLinks;
};

export default class ListeningComponent extends Component<ListeningComponentArgs> {
  @service declare auth: AuthService;
  @service declare appHeader: AppHeaderService;
  @service declare store: StoreService;
  @service declare intl: IntlService;

  constructor(owner: unknown, args: ListeningComponentArgs) {
    super(owner, args);

    this.loadData.perform();
  }

  get currentGroup(): GroupModel {
    return this.auth.currentGroup;
  }

  get taskValue(): Maybe<BrandProfileModel[]> {
    return (this.loadData.lastSuccessful?.value ?? null) as Maybe<BrandProfileModel[]>;
  }
  get hasProfilesThatSupportMentions(): boolean {
    if (!this.taskValue) {
      return false;
    }

    return (
      this.taskValue.filter(
        (profile: BrandProfileModel) => profile.profileType === 'instagram' || profile.profileType === 'tiktok'
      ).length > 0
    );
  }

  get canAccessBrandDna(): boolean {
    return this.auth.currentAccount.rolloutBrandDna;
  }

  get canAccessCompetitiveBenchmarking(): boolean {
    return Boolean(this.auth.currentAccount.canCompetitorsLists);
  }

  get canAccessFutureInsights(): boolean {
    return Boolean(this.auth.currentAccount.canFutureInsights && this.auth.currentAccount.rolloutFutureInsights);
  }

  get canAccessSocialListening(): boolean {
    return Boolean(this.auth.currentAccount.canSocialListening);
  }

  get socialListeningLinks(): SocialListeningSubNavLinks[] {
    return socialListeningLinks(
      this.intl,
      {
        isSocialListeningVisible: this.canAccessSocialListening,
        isFutureInsightsVisible: this.canAccessFutureInsights,
        isMentionsVisible: this.canAccessMentions,
        isSentimentVisible: this.canAccessSentiment,
        isBrandDnaVisible: this.canAccessBrandDna,
        isCompetitiveBenchmarkingVisible: this.canAccessCompetitiveBenchmarking
      },
      this.auth.currentAccount.canSocialListening ? undefined : 'header.navigation.social_listening.benchmarking_longer'
    );
  }

  get canAccessMentions(): boolean {
    return Boolean(
      this.hasProfilesThatSupportMentions &&
        (this.auth.currentAccount.canMyBrandMentions || this.canAccessSocialListening)
    );
  }

  get canAccessSentiment(): boolean {
    return Boolean(this.auth.currentAccount.canMyBrandSentiment);
  }

  @action
  hideFlyout(): void {
    this.appHeader.hideFlyout();
  }

  loadData = task(async () => {
    try {
      const profiles = await this.store.query('brand-profile', { group_id: this.currentGroup.get('id') });

      return profiles;
    } catch (error) {
      return null;
    }
  });
}
